import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup';
import * as global from 'brokertslib/src/helpers/global'

import { Button, useDisclosure } from '@chakra-ui/react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { MainContent } from '../../components/MainContent'
import { FormButtons } from '../FormButtons'
import { Save } from '../../icons'
import { ExtraInsuranceProperty } from '../ExtraInsuranceProperty'
import { Discounts } from '../discounts/Discounts'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { useEffect, useState } from 'react'
import { StepBase } from 'BrokerTsLib/src/dtos/StepBase'
import { InsuranceParamsPropertyDto, Step2Property } from 'BrokerTsLib/src/dtos'
import { useUpdateLastWizardStep, useSaveStep2 } from '../../hooks/useWizard'
import { useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'
import { FormSpacer } from '../../components/FormSpacer'
import ErrorSummary from '../../components/ErrorSummary'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'


export type Step2FormValues = Step2Property & StepBase

export const Step2Form = () => {
  const navigate = useNavigate()
  const { insuranceParams, insuranceParamsId: paramsId, refetchInsuranceParams } = useInsuranceParamsContext()
  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)

  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { mutateAsync: submitForm, isError: isSubmitError } = useSaveStep2()

  const methods = useForm<Step2FormValues>({
    resolver: yupResolver(yup.object().shape({})) as any, // aby se pripadne resetovaly serverove validace
    mode: 'onBlur',
    defaultValues: {
      property: (insuranceParams as InsuranceParamsPropertyDto).property
    },
  })
  const {
    handleSubmit,
    getValues,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = methods

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting]);

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const handlePostError = (data: any) => {
    Object.keys(data).forEach((x: any) => {
      if (x.toString().toLowerCase().startsWith('discounts[')) {
        setError(x.toLowerCase(), { message: data[x].join(' ') })
      }
      else {
        setError(global.toCamelCase(x), { message: data[x].join(' ') })
      }
    })
  }

  const onSubmitForm = handleSubmit((data) => {
    return submitForm({ ...data, paramsId: paramsId ?? "" })
      .then((response) => {
        if (data.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 2) < 3) {
            updateStep({ paramsId: Number(paramsId), step: 3, stepsStep: 'carkulka' }).then(() => {
              refetchInsuranceParams().then(() => {
                navigate(`/chaloupka/wizard/3/${paramsId}`)
              })
            })
          }
          else {
            navigate(`/chaloupka/wizard/3/${paramsId}`)
          }
        }
        else {
          savedFormAlertOnOpen()
        }
      })
      .catch((err: AxiosError) => {
        if (!(err.response && err.response.status === 400 && err.response.data)) {
          savedFormAlertOnOpen()
          return;
        }

        let data = err.response.data
        handlePostError(data);
      })
      .finally(() => { })
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Připojištění</FormSectionTitle>

              <FormSectionDescription>
                Vyberte limity zvoleného připojišťění.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <ExtraInsuranceProperty />
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Slevy pojištění</FormSectionTitle>

              <FormSectionDescription>
                Nastavte slevy pojistníka a jednotlivých pojišťoven.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <Discounts />
            </FormSectionBody>
          </FormSection>

          <FormButtons
            rightButtons={
              <>
                <Button variant="outline"
                  disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                  isLoading={isSubmittingCurrent}
                  loadingText="Ukládám..."
                  type="button"
                  onClick={() => {
                    setValue('nextPage', 'current')
                    setIsSubmittingCurrent(true)
                    onSubmitForm()
                  }}>
                  Uložit
                </Button>

                <Button
                  colorScheme="primary"
                  isLoading={isSubmittingNext}
                  disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                  loadingText="Počítám nabídky..."
                  type="button"
                  onClick={() => {
                    setValue('nextPage', 'next')
                    setIsSubmittingNext(true)
                    onSubmitForm()
                  }}>
                  <Save />
                  Uložit a spočítat nabídky
                </Button>

                <SubmitDialog
                  isOpen={isSavedFormAlertOpen}
                  onClose={savedFormAlertOnClose}
                  colorScheme={!isSubmitError ? 'primary' : "red"}
                  header={!isSubmitError ? 'Uloženo' : "Chyba"}
                  body={!isSubmitError ? 'Parametry nabídky byly úspěšně uloženy' : "Při ukládání došlo k chybě"}
                />
              </>
            }
          />
        </MainContent>

        <FormSpacer />

        <ErrorSummary errors={errors} />
      </form>
    </FormProvider>
  )
}
